import { Group, SimpleGrid, Stack, Text } from '@mantine/core';

interface Props {
  type: string;
  /**
   * Объект с type-specific полями. Например `client.legalRu` или
   * `managerClientDetail.details`. Ключи зависят от типа клиента.
   */
  fields: Record<string, unknown> | null | undefined;
  /** Контакты для блока внизу. Опц. */
  contact?: {
    phone?: string | null;
    email?: string | null;
    actualAddress?: string | null;
  };
}

/**
 * Универсальный рендер полей идентификации клиента по его типу.
 * Используется и в кабинете клиента (Профиль), и в карточке у менеджера.
 */
export function ClientFieldsByType({ type, fields, contact }: Props) {
  const d = (fields ?? {}) as Record<string, unknown>;

  const Field = ({ label, value }: { label: string; value: unknown }) => {
    const v = typeof value === 'string' ? value : value == null ? '' : String(value);
    if (!v) return null;
    return (
      <div>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Text size="sm">{v}</Text>
      </div>
    );
  };

  const contacts =
    contact && (contact.phone || contact.email || contact.actualAddress) ? (
      <Stack gap={4} mt="xs">
        <Group gap="md">
          {contact.phone && <Text size="sm">📞 {contact.phone}</Text>}
          {contact.email && <Text size="sm">✉️ {contact.email}</Text>}
        </Group>
        {contact.actualAddress && (
          <Text size="sm">📍 Фактический адрес: {contact.actualAddress}</Text>
        )}
      </Stack>
    ) : null;

  switch (type) {
    case 'INDIVIDUAL':
      return (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <Field label="Фамилия" value={d.lastName} />
            <Field label="Имя" value={d.firstName} />
            <Field label="Отчество" value={d.middleName} />
            <Field label="Серия паспорта" value={d.passportSerial} />
            <Field label="Номер паспорта" value={d.passportNumber} />
            <Field
              label="Дата выдачи паспорта"
              value={
                typeof d.passportIssueDate === 'string'
                  ? new Date(d.passportIssueDate).toLocaleDateString('ru-RU')
                  : null
              }
            />
            <Field label="Кем выдан" value={d.passportIssuedBy} />
            <Field label="Адрес регистрации" value={d.registrationAddress} />
            <Field label="Адрес фактический" value={d.actualAddress} />
          </SimpleGrid>
          {contacts}
        </Stack>
      );
    case 'ENTREPRENEUR':
      return (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <Field label="Фамилия" value={d.lastName} />
            <Field label="Имя" value={d.firstName} />
            <Field label="Отчество" value={d.middleName} />
            <Field label="ИНН" value={d.inn} />
            <Field label="ОГРНИП" value={d.ogrnip} />
            <Field label="Адрес регистрации" value={d.registrationAddress} />
          </SimpleGrid>
          {contacts}
        </Stack>
      );
    case 'LEGAL_RU':
      return (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Field label="Полное наименование" value={d.fullName} />
            <Field label="Краткое наименование" value={d.shortName} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <Field label="ИНН" value={d.inn} />
            <Field label="КПП" value={d.kpp} />
            <Field label="ОГРН" value={d.ogrn} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Field label="Юридический адрес" value={d.legalAddress} />
            <Field label="ФИО руководителя" value={d.directorName} />
          </SimpleGrid>
          {contacts}
        </Stack>
      );
    case 'LEGAL_FOREIGN':
      return (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Field label="Полное наименование" value={d.fullName} />
            <Field label="Краткое наименование" value={d.shortName} />
            <Field label="Страна" value={d.country} />
            <Field label="Tax ID" value={d.taxId} />
            <Field label="Регистрационный номер" value={d.registrationId} />
            <Field label="Адрес" value={d.address} />
            <Field label="Филиал/представительство в РФ" value={d.ruBranch} />
            <Field label="Контактное лицо" value={d.contactPersonName} />
            <Field label="Телефон контактного лица" value={d.contactPersonPhone} />
            <Field label="Email контактного лица" value={d.contactPersonEmail} />
          </SimpleGrid>
          {contacts}
        </Stack>
      );
    default:
      return <Text c="dimmed">Нет данных идентификации.</Text>;
  }
}
